import type { Dispatch } from 'redux';
import {
    createActionFactory,
    Feature,
    favoritesLeftNavItemId,
    isContactsOrContactListsLeftNavItem,
    deletedItemsLeftNavItemId,
    getLogger,
    createThunk,
    hashFolderId,
    getLeftNavItemType,
    LeftNavItemType,
    joinedUnifiedGroupsLeftNavId,
    ownedUnifiedGroupsLeftNavId,
    deletedUnifiedGroupsLeftNavId,
    ThunkDispatch,
    UnifiedGroupRestoreStatus,
    contactsLeftNavItemId,
} from '../../../shared';
import {
    getPersonasInFolder,
    getPersonasInSparseFolder,
    loadDeletedItemsFolder,
    getFolders,
} from '../../../hub';
import { getGroupDetailsForPartiallyRestoredGroup } from '../../../hub/actions/data/groups/unifiedGroupActions';
import {
    getAppState,
    debouncedSerializeAndPersistPeopleHubOptions,
    serializeAndPersistPeopleHubOptions,
} from '../../../appstate';
import { shouldShowFolderPaneAsOverlay } from '../../../shared/utils';
import { getBrowserWidthBucket } from 'owa-layout';

export const menuEntryClickedAction = createActionFactory<{
    folderId: string;
    folderType: LeftNavItemType;
}>('MenuEntryClickedAction', Feature.Navigation, action => ({
    folderId: hashFolderId(action.payload.folderId),
    folderType: action.payload.folderType,
}));

export const toggleLeftNavClickedAction = createActionFactory(
    'ToggleLeftNavClickedAction',
    Feature.Navigation
);

export const setOverlayFolderPaneViewAction = createActionFactory<{
    showOverlayFolderPane: boolean;
}>('SetOverlayFolderPaneViewAction', Feature.Navigation, action => ({
    showOverlayFolderPane: action.payload.showOverlayFolderPane,
}));

export const enableCAPIv3ToggledAction = createActionFactory<{
    isEnabled: boolean;
}>('EnableCAPIv3ToggledAction', Feature.Navigation);

export const menuItemClicked = (leftNavItemId: string) =>
    createThunk('MenuItemClicked', (dispatch: ThunkDispatch, getState: () => {}) => {
        const appState = getAppState(getState());
        const contactFolders = appState.data.contactFoldersState;
        const rootFolder = contactFolders.rootContactFolder;
        const shouldShowPane = shouldShowFolderPaneAsOverlay(
            appState.view.layoutState.browserWidthBucket
        );

        dispatch(
            menuEntryClickedAction({
                folderId: leftNavItemId,
                folderType: getLeftNavItemType(leftNavItemId, contactFolders),
            })
        );
        if (shouldShowPane) {
            dispatch(toggleLeftNavClicked)();
        }

        // Do not fetch personas for some menu items
        if (
            leftNavItemId === favoritesLeftNavItemId ||
            leftNavItemId === joinedUnifiedGroupsLeftNavId ||
            leftNavItemId === ownedUnifiedGroupsLeftNavId
        ) {
            return;
        }

        if (leftNavItemId === deletedUnifiedGroupsLeftNavId) {
            const unifiedGroupsMap = appState.data.groupsHubState.unifiedGroupsMap;
            const deletedGroupIds = appState.data.groupsHubState.deletedGroupsIds;
            const partiallyRestoredGroups = deletedGroupIds
                .filter(
                    groupId =>
                        unifiedGroupsMap[groupId].restoreStatus ===
                        UnifiedGroupRestoreStatus.EXO_FAILED
                )
                .map(partiallyRestoredGroupIds => unifiedGroupsMap[partiallyRestoredGroupIds]);
            getGroupDetailsForPartiallyRestoredGroup(dispatch)(partiallyRestoredGroups);
            return;
        } else if (leftNavItemId === deletedItemsLeftNavItemId) {
            loadDeletedItemsFolder(dispatch);
        } else {
            const folderIdToFetch = isContactsOrContactListsLeftNavItem(leftNavItemId)
                ? rootFolder.id
                : leftNavItemId;

            const folderToFetchIsPublicFolder = contactFolders.publicFolders.some(
                folder => folder.id === folderIdToFetch
            );

            if (folderToFetchIsPublicFolder) {
                const publicFolderMailbox =
                    appState.data.userConfiguration.settings.DefaultPublicFolderMailbox;
                if (folderIdToFetch === null) {
                    logPublicFolderIdError('Folder Id is null');
                } else if (folderIdToFetch === undefined) {
                    logPublicFolderIdError('Folder Id is undefined');
                } else if (folderIdToFetch === '') {
                    logPublicFolderIdError('Folder Id is empty string');
                }

                getFolders(dispatch)([folderIdToFetch], publicFolderMailbox);
            }

            const folderToFetchIsSparseFolder = contactFolders.sparseFolders.some(
                folder => folder.id === folderIdToFetch
            );

            if (folderToFetchIsSparseFolder) {
                getPersonasInSparseFolder(dispatch)(folderIdToFetch);
            } else {
                getPersonasInFolder(dispatch)(folderIdToFetch);
            }
        }
    });

function logPublicFolderIdError(error: string) {
    getLogger().log('LeftNavActions', {
        component: 'publicFolder',
        error: error,
    });
}

export const toggleLeftNavClicked = (dispatch: Dispatch) => () => {
    const isOverlay = shouldShowFolderPaneAsOverlay(getBrowserWidthBucket());
    dispatch(toggleLeftNavClickedAction());
    dispatch(
        setOverlayFolderPaneViewAction({
            showOverlayFolderPane: isOverlay ? undefined : false,
        })
    );
    debouncedSerializeAndPersistPeopleHubOptions(dispatch);
};

export const updateOverlayPaneStatus = (dispatch: Dispatch) => (overlayStatus: boolean) => {
    dispatch(
        setOverlayFolderPaneViewAction({
            showOverlayFolderPane: overlayStatus,
        })
    );
};

export const enableCAPIv3Toggled = (isEnabled: boolean) =>
    createThunk('EnableCAPIv3ToggledAction', (dispatch: ThunkDispatch, getState: () => {}) => {
        dispatch(enableCAPIv3ToggledAction({ isEnabled }));
        dispatch(serializeAndPersistPeopleHubOptions)();
        const appState = getAppState(getState());
        const favoritePersonasCount =
            appState.data.hubState.favoritePersonsState.favoritePersons.length;

        // Programatically invoke click action to update the hublist with contacts
        // from CAPIv2/CAPIv3 based on toggle value or go to the favorites tab
        dispatch(
            menuItemClicked(favoritePersonasCount ? favoritesLeftNavItemId : contactsLeftNavItemId)
        );
    });

export type { Action } from '../../../shared';
